import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Bar, ResponsiveContainer, Label } from "recharts";
import { Button, Typography, Paper, Stack, useTheme, Box, Autocomplete, TextField } from "@mui/material";
import { readAsyncStorageValues } from "features/service/service.slice";
import { getMeanTimeBetweenFailure, getMeanTimeToRepair, getTotalMeanTimeBetweenFailure, getTotalMeanTimeToRepair, getMachines } from "features/service/service.slice";

const MTBFvsMTTR = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { meanTimeBetweenFailure, meanTimeToRepair, totalMeanTimeBetweenFailure, totalMeanTimeToRepair, machinesData, appConstants } = useSelector((state) => state.service);
  const [machineList, setMachineList] = useState([]);
  const [machineIdsList, setMachineIdsList] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState({ id: "all", name: "All Machines" });
  const { mode } = useSelector((state) => state.globalState);
  const [chartData, setChartData] = useState([
    { name: "MTBF", value: 0 },
    { name: "MTTR", value: 0 }
  ]);

  useEffect(() => {
    dispatch(getMachines({ breakdownOnly: true }));
    dispatch(readAsyncStorageValues());
  }, [dispatch]);

  useEffect(() => {
    if (machinesData) {
      console.log("machinesData:", machinesData.filter(ii=> ii.info));
      const modifiedMachineData = machinesData.map((item) => ({
          ...item,
          id: item.id,
          name: `${item.maintenanceCode || ""} - ${item.manufacturer}, ${item.model} [${item.serialNumber}]`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setMachineList([{ id: "all", name: "All Machines" }, ...modifiedMachineData]);
      setMachineIdsList(modifiedMachineData.map((item) => item.id));
    }
  }, [machinesData]);

  useEffect(() => {
    if (machineIdsList.length > 0) {
      const allMachine = { id: "all", name: "All Machines" };
      setSelectedMachine(allMachine);
      onChangeMachine(allMachine);
    }
  }, [machineIdsList]);

  const onChangeMachine = async (machine) => {
    setSelectedMachine(machine);
  
    if (machine?.id === "all") {
      const mtbf = await dispatch(getTotalMeanTimeBetweenFailure({ machineList: machineIdsList })).unwrap();
      const mttr = await dispatch(getTotalMeanTimeToRepair({ machineList: machineIdsList })).unwrap();
  
      setChartData([
        { name: "MTBF", value: mtbf.getMeanTimeBetweenFailure },
        { name: "MTTR", value: mttr.totalMeanTimeToRepair }
      ]);
      // console.log("all mtbf:", mtbf.getMeanTimeBetweenFailure);
      // console.log("all mttr:", mttr.totalMeanTimeToRepair);
    } else if (machine?.id) {
      const mtbf = await dispatch(getMeanTimeBetweenFailure(machine.id)).unwrap();
      const mttr = await dispatch(getMeanTimeToRepair(machine.id)).unwrap();
  
      setChartData([
        { name: "MTBF", value: mtbf.meanTimeBetweenFailuresInHours },
        { name: "MTTR", value: mttr.meanRepairTimeInHours }
      ]);
      // console.log(machine.id, " mtbf:", mtbf.meanTimeBetweenFailuresInHours);
      // console.log(machine.id," mttr:", mttr.meanRepairTimeInHours);
    }
  };

  return (
    <Stack alignItems={"center"} sx={{ mt: 5 }}>
      <Box width={1120}>
        <Paper elevation={8} sx={{ padding: 1 }}>

          <Typography variant="h4" align="center" sx={{ mb: 4, mt: 2 }}>
            Mean Time Between Failure & Mean Time To Repair
          </Typography>

          <Stack flexDirection="row" justifyContent="center" sx={{ mb: 4 }} flexWrap={"wrap"} gap={2}>
            <Autocomplete
              disablePortal
              id="machine-list"
              getOptionLabel={(option) => option?.name || ""}
              options={machineList.filter((item) => item.name)}
              value={selectedMachine}
              onChange={(event, newValue) => {
                onChangeMachine(newValue);
              }}
              renderInput={(params) => <TextField {...params} label="Select Machine" />}
              sx={{ minWidth: 550, maxWidth: 800 }}
            />
          </Stack>

          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>

            <ResponsiveContainer width="90%" height={300}>
                <ComposedChart width={1050} height={400} data={chartData}>
                  <CartesianGrid stroke="#d6d6d6" />
                  <XAxis dataKey="name" />
                  <YAxis >
                    <Label
                      style={{
                        textAnchor: "middle",
                      }}
                      angle={270}
                      dx={-25}
                      value={"Hours"}
                      stroke={mode === "dark" ? "#fff" : "#7F7F7F"}
                    />
                  </YAxis>
                  <Tooltip formatter={(value) => [`${value} hrs`, "Time"]} />
                  <Bar dataKey="value" fill={theme.palette.secondary[300]} barSize={30} />
                </ComposedChart>
            </ResponsiveContainer>
              
          </div>

        </Paper>
      </Box>
    </Stack>
  );
};

export default MTBFvsMTTR;